import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { LoanOfficer } from '../types';

const PARAM_STORAGE_KEY = 'lumin_portal_params';

interface StoredParams {
  user_id?: string | null;
  location_id?: string | null;
}

const persistParams = (params: StoredParams) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(PARAM_STORAGE_KEY, JSON.stringify(params));
  } catch {
    // Storage unavailable
  }
};

const getStoredParams = (): StoredParams | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PARAM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

interface AuthContextType {
  user: User | null;
  loanOfficer: LoanOfficer | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isVA: boolean;
  canAccessAdmin: boolean; // true for both admin and VA roles
  mustChangePassword: boolean;
  urlParams: URLSearchParams | null;
  // Impersonation (View As) feature
  isImpersonating: boolean;
  impersonatedLO: LoanOfficer | null;
  realUser: LoanOfficer | null; // The admin's actual profile when impersonating
  startImpersonation: (loId: string) => Promise<void>;
  stopImpersonation: () => void;
  signOut: () => Promise<void>;
  refreshLoanOfficer: () => Promise<void>;
  clearMustChangePassword: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const IMPERSONATION_KEY = 'lumin_impersonation';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loanOfficer, setLoanOfficer] = useState<LoanOfficer | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [urlParams] = useState<URLSearchParams | null>(null);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedLO, setImpersonatedLO] = useState<LoanOfficer | null>(null);
  const [realUser, setRealUser] = useState<LoanOfficer | null>(null);

  const fetchLoanOfficerByUserId = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, location_id, full_name, first_name, last_name, phone, email, nmls, nmls_number, role, headshot, user_role, created_at, email_signature_url, must_change_password')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        return null;
      }

      return data as LoanOfficer | null;
    } catch {
      return null;
    }
  };

  const fetchLoanOfficerByLocationId = async (locationId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, location_id, full_name, first_name, last_name, phone, email, nmls, nmls_number, role, headshot, user_role, created_at, email_signature_url')
        .eq('location_id', locationId)
        .maybeSingle();

      if (error) {
        return null;
      }

      return data as LoanOfficer | null;
    } catch {
      return null;
    }
  };

  const refreshLoanOfficer = async () => {
    const params = new URLSearchParams(window.location.search);
    let userId = params.get('user_id');
    let locationId = params.get('location_id');

    if (!userId && !locationId) {
      const stored = getStoredParams();
      userId = stored?.user_id ?? null;
      locationId = stored?.location_id ?? null;
    } else {
      persistParams({ user_id: userId, location_id: locationId });
    }

    if (userId) {
      const lo = await fetchLoanOfficerByUserId(userId);
      if (lo) {
        setLoanOfficer(lo);
        return;
      }
    }

    if (locationId) {
      const lo = await fetchLoanOfficerByLocationId(locationId);
      setLoanOfficer(lo);
    }
  };

  // Fetch profile when user changes
  useEffect(() => {
    if (!user) {
      setLoanOfficer(null);
      return;
    }

    const fetchProfile = async () => {
      const profile = await fetchLoanOfficerByUserId(user.id);
      if (profile) {
        setLoanOfficer(profile);
      }
      setLoading(false);
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    // Skip auth for prospect routes
    const isProspectRoute = window.location.pathname === '/onboarding-form' ||
                            window.location.pathname === '/onboarding-success' ||
                            window.location.pathname === '/submission-success';

    if (isProspectRoute) {
      setLoading(false);
      return;
    }

    // Set up auth state listener - keep it simple, no async
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (!session) {
        setLoading(false);
      }
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setSession(session);
        setUser(session.user);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setLoanOfficer(null);
    setSession(null);
  };

  const clearMustChangePassword = () => {
    if (loanOfficer) {
      setLoanOfficer({ ...loanOfficer, must_change_password: false });
    }
  };

  // Start impersonating a loan officer (View As feature)
  const startImpersonation = async (loId: string) => {
    if (!loanOfficer) return;

    // Save the real admin user
    setRealUser(loanOfficer);

    // Fetch the LO to impersonate
    const lo = await fetchLoanOfficerByUserId(loId);
    if (lo) {
      setImpersonatedLO(lo);
      setLoanOfficer(lo);
      setIsImpersonating(true);

      // Persist impersonation state to sessionStorage
      sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify({
        impersonatedLoId: loId,
        realUserId: loanOfficer.id,
      }));
    }
  };

  // Stop impersonating and return to admin view
  const stopImpersonation = () => {
    if (realUser) {
      setLoanOfficer(realUser);
    }
    setIsImpersonating(false);
    setImpersonatedLO(null);
    setRealUser(null);
    sessionStorage.removeItem(IMPERSONATION_KEY);
  };

  // Restore impersonation state from sessionStorage on mount
  useEffect(() => {
    const restoreImpersonation = async () => {
      try {
        const stored = sessionStorage.getItem(IMPERSONATION_KEY);
        if (stored && loanOfficer) {
          const { impersonatedLoId, realUserId } = JSON.parse(stored);

          // Only restore if the current user is the real admin who started impersonation
          if (loanOfficer.id === realUserId) {
            const lo = await fetchLoanOfficerByUserId(impersonatedLoId);
            if (lo) {
              setRealUser(loanOfficer);
              setImpersonatedLO(lo);
              setLoanOfficer(lo);
              setIsImpersonating(true);
            }
          } else if (loanOfficer.id !== impersonatedLoId) {
            // Clear stale impersonation data
            sessionStorage.removeItem(IMPERSONATION_KEY);
          }
        }
      } catch {
        sessionStorage.removeItem(IMPERSONATION_KEY);
      }
    };

    if (loanOfficer && !isImpersonating) {
      restoreImpersonation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanOfficer?.id]);

  // Use realUser for role checks when impersonating (so admin still has admin permissions)
  const userForRoleCheck = realUser || loanOfficer;
  const isSuperAdmin = userForRoleCheck?.user_role === 'super_admin';
  const isAdmin = userForRoleCheck?.user_role === 'admin' || isSuperAdmin;
  const isVA = userForRoleCheck?.user_role === 'va';
  const canAccessAdmin = isAdmin || isVA;
  const mustChangePassword = loanOfficer?.must_change_password === true;

  const value = {
    user,
    loanOfficer,
    session,
    loading,
    isAdmin,
    isVA,
    canAccessAdmin,
    mustChangePassword,
    urlParams,
    // Impersonation
    isImpersonating,
    impersonatedLO,
    realUser,
    startImpersonation,
    stopImpersonation,
    signOut,
    refreshLoanOfficer,
    clearMustChangePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
